// by freakms - ich schwöre feierlich ich bin ein tunichtgut
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import {
  Warehouse,
  Plus,
  Minus,
  History,
  Package,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Layers,
  PackageMinus,
  ArrowDownCircle
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

// ---- Hilfsfunktionen ----

const getMhdStatus = (expiryDate) => {
  if (!expiryDate || expiryDate === "1970-01-01") return null;
  const today = new Date();
  const expiry = new Date(expiryDate);
  const days = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
  if (days < 0) return { status: "expired", days, label: `${Math.abs(days)}T abgelaufen`, color: "bg-red-100 text-red-800 border-red-200" };
  if (days <= 14) return { status: "critical", days, label: `${days}T`, color: "bg-amber-100 text-amber-800 border-amber-200" };
  if (days <= 30) return { status: "warning", days, label: `${days}T`, color: "bg-yellow-100 text-yellow-800 border-yellow-200" };
  return { status: "ok", days, label: `${days}T`, color: "bg-emerald-50 text-emerald-700 border-emerald-200" };
};

const formatDate = (d) => {
  if (!d || d === "1970-01-01") return null;
  return new Date(d).toLocaleDateString("de-DE");
};

// ---- Wareneingang-Dialog (neue Charge anlegen) ----

const NewBatchDialog = ({ product, onSuccess, onClose }) => {
  const [quantity, setQuantity] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [purchasePrice, setPurchasePrice] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [conflict, setConflict] = useState(null);

  // MHD-Konfliktprüfung sobald Datum eingegeben wird
  const handleExpiryChange = async (val) => {
    setExpiryDate(val);
    setConflict(null);
    if (!val || !product) return;
    try {
      const res = await axios.post(`${API}/inventory/check-mhd-conflict?product_id=${product.id}&expiry_date=${val}`);
      if (res.data?.has_conflict) setConflict(res.data);
    } catch {
      // Kein Konflikt oder Fehler – ignorieren
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const qty = parseInt(quantity);
    if (quantity === "" || isNaN(qty) || qty < 0) {
      toast.error("Bitte gültige Menge eingeben");
      return;
    }
    if (!expiryDate) {
      toast.error("MHD ist Pflichtfeld beim Wareneingang");
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/inventory/batches`, {
        product_id: product.id,
        product_name: product.name,
        quantity: qty,
        expiry_date: expiryDate,
        purchase_price_gross: purchasePrice ? parseFloat(purchasePrice) : 0,
        note: note.trim()
      });
      toast.success(`${qty} Stück eingebucht (MHD ${formatDate(expiryDate)})`);
      if (res.data?.mhd_conflict?.has_conflict) {
        toast.warning(res.data.mhd_conflict.message, { duration: 8000 });
      }
      onSuccess();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Einbuchen");
    } finally {
      setSaving(false);
    }
  };

  return (
    <DialogContent className="max-w-md">
      <DialogHeader className="pb-2">
        <DialogTitle className="font-heading text-base truncate pr-6">
          Wareneingang: {product?.name}
        </DialogTitle>
      </DialogHeader>
      <form onSubmit={handleSubmit} className="space-y-3">

        {/* MHD-Konflikt-Warnung */}
        <AnimatePresence>
          {conflict && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="bg-amber-50 border border-amber-300 rounded-md p-3 text-sm"
            >
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                <div>
                  <div className="font-medium text-amber-800">{conflict.message}</div>
                  {conflict.recommended_discount_slots?.length > 0 && (
                    <div className="mt-1 text-amber-700">
                      Empfohlene Rabattfächer: <strong>{conflict.recommended_discount_slots.join(", ")}</strong>
                    </div>
                  )}
                  {conflict.conflicts?.map((c, i) => (
                    <div key={i} className="mt-1 text-amber-600 text-xs">
                      Fach {c.slot_id}: MHD {formatDate(c.existing_expiry)} ({c.existing_days_until < 0 ? "abgelaufen" : `${c.existing_days_until} Tage`})
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Menge *</Label>
            <Input
              type="number" min="1"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              placeholder="z.B. 12"
              className="h-9 mt-1"
              data-testid="batch-quantity-input"
            />
          </div>
          <div>
            <Label className="text-xs">EK-Preis brutto (€)</Label>
            <Input
              type="number" min="0" step="0.01"
              value={purchasePrice}
              onChange={(e) => setPurchasePrice(e.target.value)}
              placeholder="0.00"
              className="h-9 mt-1"
            />
          </div>
        </div>

        <div>
          <Label className="text-xs flex items-center gap-1">
            MHD <span className="text-red-500">*</span>
            <span className="text-slate-400 font-normal ml-1">(Pflichtfeld)</span>
          </Label>
          <Input
            type="date"
            value={expiryDate}
            onChange={(e) => handleExpiryChange(e.target.value)}
            className="h-9 mt-1"
            required
            data-testid="batch-expiry-input"
          />
        </div>

        <div>
          <Label className="text-xs">Notiz</Label>
          <Input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="optional"
            className="h-9 mt-1"
          />
        </div>

        <div className="flex gap-2 pt-1">
          <Button type="button" variant="outline" onClick={onClose} className="flex-1 h-9 text-sm">
            Abbrechen
          </Button>
          <Button
            type="submit"
            disabled={saving}
            className="flex-1 h-9 text-sm bg-[#DC2626] hover:bg-[#B91C1C]"
            data-testid="batch-save-btn"
          >
            {saving ? "..." : "Einbuchen"}
          </Button>
        </div>
      </form>
    </DialogContent>
  );
};

// ---- Chargen-Zeile (aufklappbar) ----

const BatchRow = ({ batch, onExpiryUpdate }) => {
  const mhd = getMhdStatus(batch.expiry_date);
  const isPending = batch.status === "pending_expiry";

  return (
    <TableRow className={isPending ? "bg-blue-50" : ""}>
      <TableCell className="pl-8 text-slate-500 text-xs font-mono">
        {batch.id.slice(0, 8)}…
      </TableCell>
      <TableCell>
        <span className={`text-xs px-2 py-0.5 rounded-full border ${
          batch.status === "active" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
          batch.status === "pending_expiry" ? "bg-blue-50 text-blue-700 border-blue-200" :
          batch.status === "expired" ? "bg-red-50 text-red-700 border-red-200" :
          "bg-slate-100 text-slate-500 border-slate-200"
        }`}>
          {batch.status === "active" ? "Aktiv" :
           batch.status === "pending_expiry" ? "MHD offen" :
           batch.status === "expired" ? "Abgelaufen" : "Aufgebraucht"}
        </span>
      </TableCell>
      <TableCell className="font-mono font-bold text-right">{batch.quantity}</TableCell>
      <TableCell className="text-center">
        {isPending ? (
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs border-blue-300 text-blue-700"
            onClick={() => onExpiryUpdate(batch)}
          >
            MHD eintragen
          </Button>
        ) : mhd ? (
          <span className={`text-xs px-2 py-0.5 rounded border ${mhd.color}`}>
            {formatDate(batch.expiry_date)} ({mhd.label})
          </span>
        ) : (
          <span className="text-xs text-slate-400">–</span>
        )}
      </TableCell>
      <TableCell className="text-right text-xs text-slate-400">
        {new Date(batch.created_at).toLocaleDateString("de-DE")}
      </TableCell>
    </TableRow>
  );
};

// ---- MHD-Nachtragen-Dialog ----

const ExpiryUpdateDialog = ({ batch, onSuccess, onClose }) => {
  const [expiryDate, setExpiryDate] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!expiryDate) { toast.error("Bitte MHD eingeben"); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/inventory/batches/${batch.id}/expiry?expiry_date=${expiryDate}`);
      toast.success("MHD eingetragen");
      onSuccess();
      onClose();
    } catch {
      toast.error("Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  return (
    <DialogContent className="max-w-sm">
      <DialogHeader>
        <DialogTitle className="text-base">MHD nachtragen</DialogTitle>
      </DialogHeader>
      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="bg-slate-50 rounded p-2 text-sm text-slate-600">
          {batch.product_name} · {batch.quantity} Stück (geliefert {new Date(batch.created_at).toLocaleDateString("de-DE")})
        </div>
        <div>
          <Label className="text-xs">MHD *</Label>
          <Input type="date" value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)} className="h-9 mt-1" required />
        </div>
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={onClose} className="flex-1 h-9 text-sm">Abbrechen</Button>
          <Button type="submit" disabled={saving} className="flex-1 h-9 text-sm bg-[#DC2626] hover:bg-[#B91C1C]">
            {saving ? "..." : "Speichern"}
          </Button>
        </div>
      </form>
    </DialogContent>
  );
};

// ---- Entnahme-aus-Lager-Dialog ----

const REMOVAL_REASONS = [
  { value: "verderb", label: "Verderb / abgelaufen" },
  { value: "eigenverbrauch", label: "Eigenverbrauch" },
  { value: "inventur", label: "Inventurkorrektur" },
  { value: "beschaedigung", label: "Beschädigung" },
  { value: "rueckgabe", label: "Rückgabe an Lieferant" },
  { value: "sonstiges", label: "Sonstiges" },
];

const RemoveBatchDialog = ({ product, batches: productBatches, onSuccess, onClose }) => {
  const [batchId, setBatchId] = useState(productBatches[0]?.id || "");
  const [quantity, setQuantity] = useState("");
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const selectedBatch = productBatches.find(b => b.id === batchId);
  const maxQty = selectedBatch?.quantity || 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const qty = parseInt(quantity);
    if (isNaN(qty) || qty <= 0) { toast.error("Bitte gültige Menge eingeben"); return; }
    if (qty > maxQty) { toast.error(`Nur ${maxQty} Stück in dieser Charge verfügbar`); return; }
    if (!reason) { toast.error("Bitte Grund auswählen"); return; }

    const reasonLabel = REMOVAL_REASONS.find(r => r.value === reason)?.label || reason;
    setSaving(true);
    try {
      await axios.put(`${API}/inventory/${product.id}`, {
        quantity: qty,
        action: "remove",
        note: `${reasonLabel}${note ? ` – ${note}` : ""}`,
        expiry_date: null
      });
      toast.success(`${qty} Stück aus Lager entnommen (${reasonLabel})`);
      onSuccess();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Entnehmen");
    } finally {
      setSaving(false);
    }
  };

  return (
    <DialogContent className="max-w-md">
      <DialogHeader className="pb-2">
        <DialogTitle className="font-heading text-base flex items-center gap-2">
          <PackageMinus className="w-4 h-4 text-rose-600" />
          Aus Lager entnehmen: {product?.name}
        </DialogTitle>
      </DialogHeader>
      <form onSubmit={handleSubmit} className="space-y-3">

        {/* Charge wählen */}
        {productBatches.length > 1 && (
          <div>
            <Label className="text-xs">Charge</Label>
            <Select value={batchId} onValueChange={setBatchId}>
              <SelectTrigger className="h-9 mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {productBatches.map(b => {
                  const mhd = getMhdStatus(b.expiry_date);
                  return (
                    <SelectItem key={b.id} value={b.id}>
                      MHD {formatDate(b.expiry_date) || "offen"} · {b.quantity} Stück
                      {mhd && ` · ${mhd.label}`}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Aktueller Bestand */}
        <div className="bg-slate-50 rounded p-2 flex justify-between items-center text-sm">
          <span className="text-slate-500">Verfügbar in Charge:</span>
          <span className="font-bold">{maxQty} Stück</span>
        </div>

        {/* Menge */}
        <div>
          <Label className="text-xs">Menge *</Label>
          <Input
            type="number"
            min="1"
            max={maxQty}
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            placeholder={`1 – ${maxQty}`}
            className="h-9 mt-1"
            data-testid="remove-quantity-input"
          />
        </div>

        {/* Grund */}
        <div>
          <Label className="text-xs">Grund *</Label>
          <Select value={reason} onValueChange={setReason}>
            <SelectTrigger className="h-9 mt-1" data-testid="remove-reason-select">
              <SelectValue placeholder="Grund auswählen" />
            </SelectTrigger>
            <SelectContent>
              {REMOVAL_REASONS.map(r => (
                <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Notiz */}
        <div>
          <Label className="text-xs">Notiz (optional)</Label>
          <Input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="z.B. Fach A1, Charge vom 01.01."
            className="h-9 mt-1"
          />
        </div>

        <div className="flex gap-2 pt-1">
          <Button type="button" variant="outline" onClick={onClose} className="flex-1 h-9 text-sm">
            Abbrechen
          </Button>
          <Button
            type="submit"
            disabled={saving}
            className="flex-1 h-9 text-sm bg-[#DC2626] hover:bg-[#B91C1C]"
            data-testid="remove-save-btn"
          >
            {saving ? "..." : "Entnehmen"}
          </Button>
        </div>
      </form>
    </DialogContent>
  );
};

// ---- Haupt-Seite ----

const InventoryPage = () => {
  const [batches, setBatches] = useState([]);
  const [logs, setLogs] = useState([]);
  const [products, setProducts] = useState([]);
  const [conflicts, setConflicts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedProducts, setExpandedProducts] = useState({});
  const [addDialogProduct, setAddDialogProduct] = useState(null);
  const [expiryDialogBatch, setExpiryDialogBatch] = useState(null);
  const [removeDialogProduct, setRemoveDialogProduct] = useState(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [expiryDialogOpen, setExpiryDialogOpen] = useState(false);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [batchRes, logRes, prodRes, conflictRes] = await Promise.all([
        axios.get(`${API}/inventory/batches`),
        axios.get(`${API}/inventory/logs?limit=50`),
        axios.get(`${API}/products`),
        axios.get(`${API}/inventory/mhd-conflicts`).catch(() => ({ data: { conflicts: [] } }))
      ]);
      setBatches(batchRes.data);
      setLogs(logRes.data);
      setProducts(prodRes.data);
      setConflicts(conflictRes.data.conflicts || []);
    } catch {
      toast.error("Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  };

  // Chargen nach Produkt gruppieren
  const grouped = batches.reduce((acc, b) => {
    if (!acc[b.product_id]) {
      const product = products.find(p => p.id === b.product_id);
      acc[b.product_id] = { product, batches: [], totalQty: 0 };
    }
    acc[b.product_id].batches.push(b);
    acc[b.product_id].totalQty += b.quantity;
    return acc;
  }, {});

  // Produkte ohne Chargen ebenfalls anzeigen (Lagerbestand 0)
  products.forEach(p => {
    if (!grouped[p.id]) grouped[p.id] = { product: p, batches: [], totalQty: 0 };
  });

  const groupedList = Object.values(grouped)
    .filter(g => g.product)
    .sort((a, b) => a.product.name.localeCompare(b.product.name));

  const totalValue = groupedList.reduce((sum, g) => sum + g.totalQty * (g.product?.price || 0), 0);
  const pendingMhd = batches.filter(b => b.status === "pending_expiry").length;

  const toggleExpand = (pid) => setExpandedProducts(prev => ({ ...prev, [pid]: !prev[pid] }));

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-600" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="inventory-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-heading font-bold text-slate-900 tracking-tight">Lager</h1>
        <p className="text-slate-500 mt-1">Lagerbestand und Chargen</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="text-sm text-slate-500">Produkte</div>
            <div className="text-2xl font-heading font-bold">{groupedList.length}</div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="text-sm text-slate-500">Gesamtmenge</div>
            <div className="text-2xl font-heading font-bold text-blue-600">
              {batches.reduce((s, b) => s + b.quantity, 0)} Stück
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="text-sm text-slate-500">Lagerwert</div>
            <div className="text-2xl font-heading font-bold text-emerald-600">{totalValue.toFixed(2)}€</div>
          </CardContent>
        </Card>
        <Card className={`border ${pendingMhd > 0 ? "border-blue-300 bg-blue-50" : "border-slate-200"}`}>
          <CardContent className="p-4">
            <div className="text-sm text-slate-500">MHD offen</div>
            <div className={`text-2xl font-heading font-bold ${pendingMhd > 0 ? "text-blue-600" : "text-slate-400"}`}>
              {pendingMhd}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* MHD-Konflikte */}
      {conflicts.length > 0 && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <Card className="border-amber-300 bg-amber-50">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading flex items-center gap-2 text-amber-700 text-base">
                <AlertTriangle className="w-4 h-4" />
                MHD-Konflikte im Automaten ({conflicts.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {conflicts.map((c, i) => (
                <div key={i} className="flex items-center gap-3 text-sm">
                  <span className="font-medium text-amber-800">{c.product_name}</span>
                  <span className="text-amber-600">Fächer: {c.slots.join(", ")}</span>
                  <span className="text-amber-500">MHDs: {c.expiry_dates.map(formatDate).join(" / ")}</span>
                </div>
              ))}
              <p className="text-xs text-amber-600 mt-1">Empfehlung: Ältere Ware in Rabattfächer A1–A3 umlagern.</p>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Chargen-Tabelle */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <Layers className="w-5 h-5" />
              Lagerbestand nach Chargen
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Produkt / Charge</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Menge</TableHead>
                    <TableHead className="text-center">MHD</TableHead>
                    <TableHead className="text-right">Datum</TableHead>
                    <TableHead className="text-right">Aktion</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {groupedList.map(({ product, batches: productBatches, totalQty }) => {
                    const isExpanded = expandedProducts[product.id];
                    const hasConflict = conflicts.some(c => c.product_id === product.id);
                    const hasPending = productBatches.some(b => b.status === "pending_expiry");
                    const earliestMhd = productBatches
                      .filter(b => b.expiry_date && b.expiry_date !== "1970-01-01")
                      .map(b => b.expiry_date)
                      .sort()[0];
                    const mhd = getMhdStatus(earliestMhd);

                    return [
                      // Produktzeile
                      <TableRow
                        key={`prod-${product.id}`}
                        className={`cursor-pointer hover:bg-slate-50 ${hasConflict ? "bg-amber-50" : ""}`}
                        onClick={() => toggleExpand(product.id)}
                      >
                        <TableCell className="font-medium flex items-center gap-2">
                          {isExpanded
                            ? <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />
                            : <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />
                          }
                          {product.name}
                          {hasConflict && <Badge variant="outline" className="text-amber-600 border-amber-300 text-xs">MHD-Konflikt</Badge>}
                          {hasPending && <Badge variant="outline" className="text-blue-600 border-blue-300 text-xs">MHD offen</Badge>}
                          {productBatches.length > 1 && (
                            <span className="text-xs text-slate-400">{productBatches.length} Chargen</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <span className="text-xs text-slate-400">{product.category || ""}</span>
                        </TableCell>
                        <TableCell className="text-right font-mono font-bold">
                          <span className={totalQty < (product.min_stock || 10) ? "text-amber-600" : "text-slate-900"}>
                            {totalQty}
                          </span>
                        </TableCell>
                        <TableCell className="text-center">
                          {mhd ? (
                            <span className={`text-xs px-2 py-0.5 rounded border ${mhd.color}`}>
                              {formatDate(earliestMhd)} ({mhd.label})
                            </span>
                          ) : <span className="text-xs text-slate-400">–</span>}
                        </TableCell>
                        <TableCell />
                        <TableCell className="text-right">
                          <div className="flex gap-2 justify-end">
                            {/* Entnehmen */}
                            {totalQty > 0 && (
                              <Dialog
                                open={removeDialogOpen && removeDialogProduct?.id === product.id}
                                onOpenChange={(open) => { setRemoveDialogOpen(open); if (!open) setRemoveDialogProduct(null); }}
                              >
                                <DialogTrigger asChild>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="border-rose-200 text-rose-600 hover:bg-rose-50"
                                    onClick={(e) => { e.stopPropagation(); setRemoveDialogProduct({ id: product.id, name: product.name }); setRemoveDialogOpen(true); }}
                                    data-testid={`remove-batch-${product.id}`}
                                  >
                                    <PackageMinus className="w-4 h-4 mr-1" /> Entnehmen
                                  </Button>
                                </DialogTrigger>
                                {removeDialogProduct?.id === product.id && (
                                  <RemoveBatchDialog
                                    product={product}
                                    batches={productBatches.filter(b => b.quantity > 0 && ["active", "pending_expiry"].includes(b.status))}
                                    onSuccess={fetchData}
                                    onClose={() => { setRemoveDialogOpen(false); setRemoveDialogProduct(null); }}
                                  />
                                )}
                              </Dialog>
                            )}
                            {/* Wareneingang */}
                            <Dialog
                              open={addDialogOpen && addDialogProduct?.id === product.id}
                              onOpenChange={(open) => { setAddDialogOpen(open); if (!open) setAddDialogProduct(null); }}
                            >
                              <DialogTrigger asChild>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={(e) => { e.stopPropagation(); setAddDialogProduct(product); setAddDialogOpen(true); }}
                                  data-testid={`add-batch-${product.id}`}
                                >
                                  <Plus className="w-4 h-4 mr-1" /> Wareneingang
                                </Button>
                              </DialogTrigger>
                              {addDialogProduct?.id === product.id && (
                                <NewBatchDialog
                                  product={product}
                                  onSuccess={fetchData}
                                  onClose={() => { setAddDialogOpen(false); setAddDialogProduct(null); }}
                                />
                              )}
                            </Dialog>
                          </div>
                        </TableCell>
                      </TableRow>,

                      // Chargen-Zeilen (aufgeklappt)
                      ...(isExpanded ? productBatches.map(batch => (
                        <TableRow key={`batch-${batch.id}`} className="bg-slate-50">
                          <TableCell colSpan={6} className="p-0">
                            <Table>
                              <TableBody>
                                <BatchRow
                                  batch={batch}
                                  onExpiryUpdate={(b) => { setExpiryDialogBatch(b); setExpiryDialogOpen(true); }}
                                />
                              </TableBody>
                            </Table>
                          </TableCell>
                        </TableRow>
                      )) : [])
                    ];
                  })}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* MHD-Nachtragen-Dialog */}
      <Dialog open={expiryDialogOpen} onOpenChange={(open) => { setExpiryDialogOpen(open); if (!open) setExpiryDialogBatch(null); }}>
        {expiryDialogBatch && (
          <ExpiryUpdateDialog
            batch={expiryDialogBatch}
            onSuccess={fetchData}
            onClose={() => { setExpiryDialogOpen(false); setExpiryDialogBatch(null); }}
          />
        )}
      </Dialog>      {/* Bewegungsprotokoll */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.1 }}>
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <History className="w-5 h-5" />
              Bewegungsprotokoll
            </CardTitle>
          </CardHeader>
          <CardContent>
            {logs.length > 0 ? (
              <div className="space-y-0">
                {logs.map((log) => (
                  <div key={log.id} className="flex items-center gap-4 py-3 border-b border-slate-100 last:border-0">
                    <div className={`w-10 h-10 rounded-md flex items-center justify-center ${log.quantity_change > 0 ? "bg-emerald-100" : "bg-rose-100"}`}>
                      {log.quantity_change > 0
                        ? <Plus className="w-5 h-5 text-emerald-600" />
                        : <Minus className="w-5 h-5 text-rose-600" />
                      }
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{log.product_name}</div>
                      <div className="text-sm text-slate-500">
                        {log.note || (log.action === "add" ? "Wareneingang" : log.action === "automat_refill" ? "Auffüllung Automat" : "Entnahme")}
                        {log.user_name && <span className="ml-2 text-slate-400">von {log.user_name}</span>}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className={`font-mono font-bold ${log.quantity_change > 0 ? "text-emerald-600" : "text-rose-600"}`}>
                        {log.quantity_change > 0 ? "+" : ""}{log.quantity_change}
                      </div>
                      <div className="text-xs text-slate-400">{new Date(log.created_at).toLocaleDateString("de-DE")}</div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <History className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">Noch keine Bewegungen</p>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default InventoryPage;
